import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { getAlternateLinks } from '@/utils/localization';

/** each file must exist in `static/fonts/` */
const fontFilesInter = ['inter-variable', 'inter-italic-variable'].map(
  (fileName) => `inter/${fileName}`
);
const fontFilesPoppins = [
  'poppins-bold',
  'poppins-bold-italic',
  'poppins-light',
  'poppins-light-italic',
  'poppins-medium',
  'poppins-medium-italic',
  'poppins-regular',
  'poppins-regular-italic',
  'poppins-semibold',
  'poppins-semibold-italic',
].map((fileName) => `poppins/${fileName}`);

const fontFiles = [...fontFilesInter, ...fontFilesPoppins] as const;

export type MetaTagParams = Queries.SEO_METAFragment &
  Queries.TWITTER_METAFragment &
  Queries.OPENGRAPH_METAFragment;

const MetaTags = ({
  language = 'en',
  metadata,
  shouldNotIndex,
  hasRssFeed,
  isTranslated,
}: {
  language?: string;
  metadata: MetaTagParams &
    Partial<Pick<Queries.GENERAL_PAGE_TEMPLATEFragment['elements'], 'rss_feed_entries'>>;
  shouldNotIndex?: PageContext['shouldNotIndex'];
  hasRssFeed?: boolean;
  isTranslated?: boolean;
}) => {
  const { site, kontentItemRoot, allKenticoLink } = useStaticQuery<Queries.GlobalMetadataQuery>(
    graphql`
      query GlobalMetadata {
        site {
          siteMetadata {
            isProduction
            siteUrl
          }
        }
        kontentItemRoot {
          elements {
            site_name {
              value
            }
            fallback_meta_image {
              value {
                url
              }
            }
            fallback_og_image {
              value {
                url
              }
            }
            fallback_twitter_image {
              value {
                url
              }
            }
            twitter_site {
              value
            }
          }
        }
        allKenticoLink {
          nodes {
            codename
            url
          }
        }
      }
    `
  );

  const { pathname } = useLocation();
  const siteUrl = site!.siteMetadata!.siteUrl || '';
  const siteFullUrl = `${siteUrl}${pathname}`;
  const canonicalTargetUrl = allKenticoLink.nodes.find(
    (node) => node.codename === metadata.page_seo_snippet__canonical?.value?.system.codename || ''
  )?.url;
  const canonical = canonicalTargetUrl
    ? `${site!.siteMetadata!.siteUrl}${canonicalTargetUrl}`
    : siteFullUrl;
  const fallback = kontentItemRoot!.elements;

  const keywords = (metadata?.page_seo_snippet__meta_keywords?.value ?? [])
    .filter(
      (item): item is Queries.META_KEYWORDFragment =>
        item.__typename === 'kontent_item_meta_keyword'
    )
    .map((item) => item.elements.keyword.value.trim());

  const hasKeywords = keywords.length > 0;
  const hasJSONLD = Boolean((metadata.page_seo_snippet__meta_jsonld?.value ?? '').length);

  return (
    <>
      <html lang={language} />
      {fontFiles.map((fontFile) => (
        <link
          key={fontFile}
          rel="preload"
          as="font"
          href={`/fonts/${fontFile}.woff2`}
          type="font/woff2"
          crossOrigin="anonymous"
        />
      ))}
      {hasKeywords && <meta name="keywords" content={keywords.join(',')} />}
      <meta name="description" content={metadata.page_seo_snippet__meta_description.value} />
      <meta
        name="image"
        content={
          metadata.page_seo_snippet__meta_image?.value?.url ||
          fallback.fallback_meta_image?.value?.[0]?.url
        }
      />
      <title>{`${metadata.page_seo_snippet__meta_title.value} | ${fallback.site_name.value}`}</title>

      <meta
        name="robots"
        content={
          !site?.siteMetadata?.isProduction ||
          shouldNotIndex ||
          metadata.page_seo_snippet__seo_indexing?.value === 'prevent_google_indexing'
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview'
        }
      />
      {isTranslated &&
        getAlternateLinks(siteUrl, pathname).map((alternate) => (
          <link key={alternate.hrefLang} rel="alternate" {...alternate} />
        ))}
      <link rel="canonical" href={canonical} />
      <meta name="apple-mobile-web-app-title" content="Sonar" />
      <link rel="apple-touch-icon" href="/app-icon.png" />

      <meta property="og:type" content="website" />
      <meta
        property="og:title"
        content={
          metadata.opengraph_seo_snippet__og_title?.value ||
          metadata.page_seo_snippet__meta_title.value
        }
      />
      <meta
        property="og:description"
        content={
          metadata.opengraph_seo_snippet__og_description?.value ||
          metadata.page_seo_snippet__meta_description.value
        }
      />
      <meta property="og:url" content={siteFullUrl} />
      <meta
        property="og:image"
        content={
          metadata.opengraph_seo_snippet__og_image?.value?.url ||
          metadata.page_seo_snippet__meta_image?.value?.url ||
          fallback.fallback_og_image?.value?.[0]?.url
        }
      />

      <meta name="twitter:site" content={fallback.twitter_site.value} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content={
          metadata.twitter_seo_snippet__twitter_title?.value ||
          metadata.page_seo_snippet__meta_title.value
        }
      />
      <meta
        name="twitter:description"
        content={
          metadata.twitter_seo_snippet__twitter_description?.value ||
          metadata.page_seo_snippet__meta_description.value
        }
      />
      <meta
        name="twitter:image"
        content={
          metadata.twitter_seo_snippet__twitter_image?.value?.url ||
          metadata.page_seo_snippet__meta_image?.value?.url ||
          fallback.fallback_twitter_image?.value?.[0]?.url
        }
      />
      {hasJSONLD && (
        <script type="application/ld+json">
          {metadata.page_seo_snippet__meta_jsonld?.value
            ?.replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')}
        </script>
      )}
      {hasRssFeed && (
        <link
          rel="alternate"
          type="application/rss+xml"
          title={
            metadata.rss_feed_entries?.value?.[0]?.elements?.title?.value ||
            metadata.page_seo_snippet__meta_title.value
          }
          href="/rss.xml"
        />
      )}
      <meta name="zd-site-verification" content="c95o2emv60rf7gej21hmkn" />
    </>
  );
};

export const fragment = graphql`
  fragment TWITTER_META on kontent_snippet_twitter_seo_snippet {
    twitter_seo_snippet__twitter_description {
      value
    }
    twitter_seo_snippet__twitter_image {
      value {
        url
      }
    }
    twitter_seo_snippet__twitter_title {
      value
    }
  }

  fragment OPENGRAPH_META on kontent_snippet_opengraph_seo_snippet {
    opengraph_seo_snippet__og_description {
      value
    }
    opengraph_seo_snippet__og_image {
      value {
        url
      }
    }
    opengraph_seo_snippet__og_title {
      value
    }
  }

  fragment META_KEYWORD on kontent_item_meta_keyword {
    __typename
    elements {
      keyword {
        value
      }
    }
  }

  fragment SEO_META on kontent_snippet_page_seo_snippet {
    page_seo_snippet__meta_description {
      value
    }
    page_seo_snippet__meta_keywords {
      value {
        ...BLOG_TAG
        ...META_KEYWORD
      }
    }
    page_seo_snippet__meta_jsonld {
      value
    }
    page_seo_snippet__meta_image {
      value {
        url
      }
    }
    page_seo_snippet__meta_title {
      value
    }
    page_seo_snippet__seo_indexing {
      value
    }
    page_seo_snippet__canonical {
      value {
        ... on kontent_item {
          system {
            codename
          }
        }
      }
    }
  }
`;

export default MetaTags;
